// 개요의 "노쇠지수(스마트 프레일 지수)" 카드. 지난주 인사이트 위에 둔다(2026-10-13 사용자).
//
//   점수·등급 + 지난 지수와의 변화 → 등급 구간 눈금 → 영역 아이콘 4개(누르면 그 영역의 세부지표 점수가 펼쳐진다)
//   → AI 브리핑(지난 지수 대비 좋아진 점·주의할 점·관리 방법) → 권장 운동·식단(2단계, FrailGuideBlock).
//
// 점수는 sdss-api 가 설문 + 측정 + 건강나이로 계산해 저장한 값이다. 여기서 다시 계산하지 않는다.
// 세부지표를 처음부터 다 나열하지 않는다 — 열두 줄이 늘 펼쳐져 있으면 아래 인사이트·메모가 밀린다.
// 데이터가 없어 대체값을 쓴 항목에는 '대체값' 배지 — 담당자가 "측정이 없어서 그런 점수" 임을 알아야 한다.
// ⚠️ 점수가 높을수록 위험하다(0~100). 변화 화살표는 "내려가면 좋다" 방향이다.

import React, { useState } from 'react';
import { Activity, ClipboardList, Footprints, HeartPulse, CalendarClock, Sparkles, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import FrailGuideBlock from './FrailGuideBlock';

const GRADE_TONE = { HEALTHY: 'normal', PRE_FRAIL: 'caution', FRAIL: 'danger', HIGH_RISK: 'danger' };
const AREA_ICON = { SURVEY: ClipboardList, ACTIVITY: Footprints, METABOLIC: HeartPulse, HEALTH_AGE: CalendarClock };
const AREA_ORDER = ['SURVEY', 'ACTIVITY', 'METABOLIC', 'HEALTH_AGE'];

const shortDate = (ymd) => (ymd ? `${Number(ymd.slice(5, 7))}.${Number(ymd.slice(8, 10))}` : '');
const rangeOf = (index) => (index ? `${shortDate(index.startDate)}~${shortDate(index.endDate)}` : '');

/** 점수 차이 글. 내려가면 좋아진 것이다. */
const deltaText = (delta) => {
    if (delta === null || delta === undefined) return null;
    if (delta === 0) return '변화 없음';
    return delta < 0 ? `${Math.abs(delta)}점 좋아짐` : `${delta}점 나빠짐`;
};
const deltaTone = (delta) => (delta === null || delta === undefined || delta === 0 ? 'none' : delta < 0 ? 'normal' : 'danger');

const groupByArea = (items) => {
    const groups = new Map();
    for (const item of items) {
        const key = item.area || 'ETC';
        if (!groups.has(key)) groups.set(key, { code: key, name: item.areaName || key, items: [], score: 0, maxScore: 0, previousScore: 0, hasPrevious: false });
        const g = groups.get(key);
        g.items.push(item);
        g.score += item.score || 0;
        g.maxScore += item.maxScore || 0;
        if (item.previousScore !== null && item.previousScore !== undefined) { g.previousScore += item.previousScore; g.hasPrevious = true; }
    }
    return [...groups.values()].sort((a, b) => AREA_ORDER.indexOf(a.code) - AREA_ORDER.indexOf(b.code));
};

const BriefingBlock = ({ briefing, status, error, createdAt, onRegenerate, available }) => {
    if (!available) return null;
    return (
        <div className="detail-page__frail-brief">
            <div className="detail-page__frail-brief-head">
                <span className="detail-page__insights-badge"><Sparkles size={12} aria-hidden="true" /> AI 브리핑</span>
                {status === 'ready' && createdAt && <small className="detail-page__frail-brief-when">{createdAt} 작성</small>}
                {status === 'ready' && onRegenerate && (
                    <button type="button" className="sds-btn sds-btn--ghost sds-btn--sm detail-page__frail-brief-refresh" onClick={onRegenerate}>
                        <RefreshCw size={14} aria-hidden="true" />다시 만들기
                    </button>
                )}
            </div>
            {status === 'loading' && <p className="sds-list__empty">지난 지수와 비교한 브리핑을 만드는 중입니다. 처음에는 30초쯤 걸릴 수 있습니다.</p>}
            {status === 'failed' && <p className="sds-list__empty">브리핑을 만들지 못했습니다.{error ? ` (${error})` : ''}</p>}
            {status === 'disabled' && <p className="sds-list__empty">AI 브리핑이 꺼져 있습니다.</p>}
            {status === 'ready' && briefing && (
                <>
                    <p className="detail-page__frail-brief-summary">{briefing.summary}</p>
                    {briefing.improvements.length > 0 && (
                        <div className="detail-page__frail-brief-group">
                            <b>좋아진 점</b>
                            <ul>{briefing.improvements.map((t, i) => <li key={`i${i}`}>{t}</li>)}</ul>
                        </div>
                    )}
                    {briefing.concerns.length > 0 && (
                        <div className="detail-page__frail-brief-group">
                            <b>주의할 점</b>
                            <ul>{briefing.concerns.map((t, i) => <li key={`c${i}`}>{t}</li>)}</ul>
                        </div>
                    )}
                    {briefing.actions.length > 0 && (
                        <div className="detail-page__frail-brief-group">
                            <b>관리 방법</b>
                            <ul>{briefing.actions.map((t, i) => <li key={`a${i}`}>{t}</li>)}</ul>
                        </div>
                    )}
                    {briefing.closing && <p className="detail-page__frail-brief-closing">{briefing.closing}</p>}
                </>
            )}
        </div>
    );
};

const FrailIndexSection = ({
    index = null, previousIndex = null, assessment = null, grades = [], loading = false, error = null,
    briefing = null, briefingStatus = 'idle', briefingError = null, briefingCreatedAt = null, briefingAvailable = false, onRegenerateBriefing = null,
    guide = null, guideStatus = 'idle', guideError = null, guideCreatedAt = null, guideCached = false, guideAvailable = false, onRegenerateGuide = null,
    guideSent = null, onSendGuide = null, guideSending = false, guideSendResult = null, onPreviewGuide = null
}) => {
    const [openArea, setOpenArea] = useState(null);
    const tone = index ? (GRADE_TONE[index.grade?.code] || 'none') : 'none';
    const delta = index?.delta ?? (index && previousIndex ? index.score - previousIndex.score : null);
    const areas = index ? groupByArea(index.items) : [];

    return (
        <div className="sds-list detail-page__frail">
            <div className="sds-list__title">
                노쇠지수
                <span className="detail-page__insights-badge"><Activity size={12} aria-hidden="true" /> 스마트 프레일 지수(SFI)</span>
            </div>

            {error && <p className="sds-list__empty">노쇠지수를 불러오지 못했습니다.</p>}
            {!error && loading && <p className="sds-list__empty">불러오는 중…</p>}
            {!error && !loading && !index && (
                <p className="sds-list__empty">
                    {assessment
                        ? '프레일 평가는 있지만 아직 계산된 노쇠지수가 없습니다. 앱에서 분석결과 1개월을 열면 계산됩니다.'
                        : '아직 프레일 평가가 없습니다. 앱에서 8문항을 평가하면 노쇠지수가 여기에 나옵니다.'}
                </p>
            )}

            {index && (
                <>
                    <div className="detail-page__frail-score">
                        <div className="detail-page__frail-now">
                            <span className="detail-page__frail-label">현재 {rangeOf(index)}</span>
                            <span className="detail-page__frail-number">{index.score}<small>점</small></span>
                            <span className={`sds-badge sds-badge--${tone}`} title={index.grade?.description || undefined}><span>{index.grade?.name}</span></span>
                        </div>
                        <div className="detail-page__frail-prev">
                            {previousIndex ? (
                                <>
                                    <span className="detail-page__frail-label">지난 {rangeOf(previousIndex)}</span>
                                    <span className="detail-page__frail-prev-number">{previousIndex.score}<small>점</small></span>
                                    <span className={`sds-badge sds-badge--${GRADE_TONE[previousIndex.grade?.code] || 'none'}`}><span>{previousIndex.grade?.name}</span></span>
                                    <span className={`sds-badge sds-badge--${deltaTone(delta)} detail-page__frail-delta`}><span>{deltaText(delta)}</span></span>
                                </>
                            ) : (
                                <span className="detail-page__frail-label">첫 지수입니다. 다음 집계부터 지난 지수와 비교합니다.</span>
                            )}
                        </div>
                    </div>
                    {index.grade?.description && <div className="detail-page__frail-meaning">{index.grade.name} · {index.grade.description}</div>}

                    {grades.length > 0 && (
                        <div className="detail-page__frail-bands" role="img" aria-label={`0~100 중 ${index.score}점, ${index.grade?.name}`}>
                            {grades.map((grade) => (
                                <div
                                    key={grade.code}
                                    className={`detail-page__frail-band detail-page__frail-band--${GRADE_TONE[grade.code] || 'none'}${grade.code === index.grade?.code ? ' is-active' : ''}`}
                                    style={{ flex: Math.max(1, grade.maxScore - grade.minScore + 1) }}
                                >
                                    <span>{grade.name}</span>
                                    <small>{grade.minScore}~{grade.maxScore}</small>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="detail-page__insights-range">
                        평가 {assessment?.assessedAt || '-'}
                        {assessment?.expired && <span className="sds-badge sds-badge--caution detail-page__frail-expired"><span>재평가 필요</span></span>}
                    </div>

                    {/* 영역 아이콘. 누르면 그 영역의 세부지표가 펼쳐진다. 한 번에 하나만. */}
                    <div className="detail-page__frail-areas" role="group" aria-label="영역별 점수">
                        {areas.map((area) => {
                            const Icon = AREA_ICON[area.code] || Activity;
                            const open = openArea === area.code;
                            const areaDelta = area.hasPrevious ? area.score - area.previousScore : null;
                            return (
                                <button
                                    key={area.code}
                                    type="button"
                                    className={`detail-page__frail-area-tile${open ? ' is-open' : ''}`}
                                    aria-expanded={open}
                                    aria-controls={`frail-area-${area.code}`}
                                    onClick={() => setOpenArea(open ? null : area.code)}
                                >
                                    <span className="sds-list__icon sds-list__icon--sm sds-list__icon--info" aria-hidden="true"><Icon size={18} /></span>
                                    <span className="detail-page__frail-area-name">{area.name}</span>
                                    <span className="detail-page__frail-area-score">{area.score}<small>/{area.maxScore}</small></span>
                                    {areaDelta !== null && <span className={`detail-page__frail-area-delta detail-page__frail-area-delta--${deltaTone(areaDelta)}`}>{areaDelta === 0 ? '변화 없음' : areaDelta < 0 ? `▼${Math.abs(areaDelta)}` : `▲${areaDelta}`}</span>}
                                    {open ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
                                </button>
                            );
                        })}
                    </div>
                    {areas.filter((area) => area.code === openArea).map((area) => (
                        <div key={area.code} id={`frail-area-${area.code}`} className="detail-page__frail-area">
                            {area.items.map((item) => (
                                <div className="sds-list__row" key={item.code}>
                                    <div className="sds-list__body">
                                        <b>
                                            {item.name}
                                            {item.fallback && <span className="sds-badge sds-badge--none detail-page__frail-fallback"><span>대체값</span></span>}
                                        </b>
                                        {item.basis && <small>{item.basis}</small>}
                                    </div>
                                    <div className="sds-list__spacer" />
                                    <div className="detail-page__frail-item-scores">
                                        {item.previousScore !== null && item.previousScore !== undefined && (
                                            <span className="detail-page__frail-item-prev">지난 {item.previousScore}</span>
                                        )}
                                        <span className="sds-list__value">{item.score}/{item.maxScore}</span>
                                        {item.delta !== null && item.delta !== undefined && item.delta !== 0 && (
                                            <span className={`detail-page__frail-area-delta detail-page__frail-area-delta--${deltaTone(item.delta)}`}>{item.delta < 0 ? `▼${Math.abs(item.delta)}` : `▲${item.delta}`}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}

                    <BriefingBlock briefing={briefing} status={briefingStatus} error={briefingError} createdAt={briefingCreatedAt} onRegenerate={onRegenerateBriefing} available={briefingAvailable} />
                    <FrailGuideBlock
                        guide={guide} status={guideStatus} error={guideError} createdAt={guideCreatedAt} cached={guideCached} onRegenerate={onRegenerateGuide} available={guideAvailable}
                        sent={guideSent} onSend={onSendGuide} onPreview={onPreviewGuide} sending={guideSending} sendResult={guideSendResult}
                    />
                </>
            )}
        </div>
    );
};

export default FrailIndexSection;
